<template>
    <article>
        <p>
            We're Nik and Anton, founders of Coroot. We've built a tool that boosts the reliability engineering skills of your team.
            Think of it as your personal assistant who has not only found the root cause of an outage but also suggested a list of possible fixes.
        </p>

        <h2>Background</h2>
        <p>
            Having a background in managing IT ops teams and building a cloud monitoring platform,
            here are my observations based on my experience:
        </p>

        <ul>
            <li>
                The main purpose of monitoring tools is to help you detect and fix outages.
                Instead of doing this, most only give you the ability to configure dashboards and triggers.
                Furthermore, you still have to manually search for the needle in the haystack of metrics, logs, and traces.
            </li>
            <li>
                <p>
                    Monitoring software would do so much more if it only knew what engineers know. For instance, if a container
                    is limited in CPU time due to throttling, that means this container is performing slower.
                    Most engineers could realize that these two events are interrelated due to their understanding of the physical meaning of metrics.
                    Current tools would just overlook it.
                </p>

                <p>
                    Another limitation existing tools have is their inability to see the whole picture.
                    For example, it is advantageous to understand that an application is a set of containers that work together.
                    So, a metric describing an individual container will not tell you anything.
                    However, a metric describing all related containers would be quite revealing.
                </p>
            </li>
            <li>
                <p>
                    I've seen a lot of engineers work on fixing issues.
                    Interestingly enough, they all act practically the same. They check a similar set of hypotheses about a possible root cause,
                    confirming or rejecting them one by one: compute resources, upstream services, databases, etc.
                </p>

                <p>
                    Of course, this list is not exhaustive since each application might have its own specific issues.
                    Nevertheless, imagine if you were able to check for common pitfalls in just one second.
                </p>

            </li>
        </ul>

        <h2>Coroot</h2>

        <p>
            We've built Coroot under the belief that more than <b>80%</b> of issues can be detected automatically.
            Coroot is a virtual assistant who audits your infrastructure just like an experienced engineer would. It:
        </p>

        <ul>
            <li>
                turns metrics describing any system to a model of that system
            </li>
            <li>
                checks a set of hypotheses to confirm or reject having issues in any subsystem
            </li>
            <li>
                suggests a list of possible fixes along with helpful details concerning each particular issue
            </li>
        </ul>

        <h2>Making a model of a distributed system</h2>

        <p>
            In order to get a clear picture of a distributed system, Coroot makes a model of it.
            The first main step here is to group individual containers into services or applications.
        </p>

        <p>
            Previously, meta-information for the grouping was only available in manifests of configuration management tools, such as Chef, Ansible, and Puppet.
            Nowadays, the widespread adoption of Kubernetes makes it easier to extract this data. However, it is not enough.
        </p>

        <p>
            We also need to know how applications communicate with each other.
            Coroot uses network-level tracing to get a comprehensive map of all network connections in a cluster.
            This approach has a number of advantages over application-level tracing:
        </p>

        <ul>
            <li>
                Easy integration without the need to change code.
            </li>
            <li>
                It traces absolutely all communications, including connections between infrastructure components (e.g., message brokers and databases).
            </li>
        </ul>

        <p>
            Network tracing is a part of our open-source
            <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent</a> which is compatible with Prometheus.
        </p>

        <p>
            Below you can see a model of the <var>product-catalog</var> application. It has two instances running
            three applications communicating with it: <var>kubelet</var>, <var>prometheus</var>, and <var>frontend</var>.
            <var>Product-catalog</var> itself depends only on the <var>pg-main-1</var> database.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/introducing-coroot/model.png" />
        </v-card>

        <p>
            A model like this is already pretty useful since it gains visibility into the distributed system architecture.
            At Coroot, we went further by using application models for root cause analysis.
        </p>

        <h2 id="inspections">Model aware inspections</h2>

        <p>
            Unfortunately, using thresholds for metrics is not always possible, since not all applications are built the same.
            For instance, an application with a response time objective set at 10ms would be affected even by the slightest increase in network latency.
            So, setting a low threshold for network latency seems reasonable.
            Though, doing this may generate a large number of false positives for services resilient against such delays.
        </p>

        <p>
            Using a model, such as the one described above, empowers Coroot to audit all subsystems within an application's context.
            In other words, each inspection is aware of the instances, nodes, and services related to any particular application.
         </p>

        <p>
            For example, the <b>Storage inspection</b> checks the correlation
            between database latency and its volumes' performance:
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/introducing-coroot/correlation.png" />
        </v-card>

        <p>
            The strong correlation seen here means the database latency has been affected by the storage I/O latency.
            If an application is meeting its SLOs in the short term, it is not necessary to perform such inspections.
            This eliminates false positives in root cause analysis.
        </p>

        <p>
            The coolest thing is that this approach does not require any configuration other than the user-defined application SLOs.
        </p>


        <h2 id="fixes">Help in fixing issues</h2>

        <p>
            Coroot aims to help in troubleshooting by providing you with a list of possible fixes and useful details on every detected issue.
        </p>

        <p>
            Below is an example of the <b>Storage inspection</b>'s report:
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/introducing-coroot/troubleshooting.png" />
        </v-card>

        <p>
            As you can see, there are two primary ways to fix the issue: reduce the I/O load or increase the volume performance.
            However, Coroot went a step further and collected all the details needed to troubleshoot:
        </p>

        <ul>
            <li>The list of most I/O-intensive database queries that helps to identify which ones are necessary to optimize, eliminate or move over to a separate read replica.</li>
            <li>The details of the volume, such as storage type, size, and the number of provisioned IOPS.</li>
            <li>Links to the related documentation pages to avoid navigating through your knowledge base or googling it.</li>
        </ul>

        <h2>Conclusion</h2>

        <p>
            As we work on Coroot, we are constantly asking ourselves:
        </p>

        <ul>
            <li>What are all the possible ways to fix an outage?</li>
            <li>What do engineers need to troubleshoot efficiently?</li>
            <li>What additional information might be helpful to them?</li>
        </ul>

        <p>
            Though these questions are very basic and straightforward, chasing to answer them through a product is quite complicated.
            Nevertheless, we have come up with a truly efficient assistant that promises to make any engineer's life much easier.
        </p>

        <Banner />
    </article>
</template>

<script>


import Banner from "@/components/Banner";

export default {
    components: {Banner}
}
</script>

<style scoped>

</style>
